import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Text,
  Textarea,
  VStack,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiTrash2, FiUserCheck } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useBounty } from '../../hooks/useBounties';
import { createClaim } from '../../hooks/useClaims';
import { useStorage } from '../../hooks/useStorage';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Price } from '../common/Price';

export default function SubmitClaim() {
  const { bountyId } = useParams();
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { bounty, loading: bountyLoading } = useBounty(bountyId);
  const { uploadFiles, uploading, progress } = useStorage();
  const navigate = useNavigate();
  const toast = useToast();

  const [researchFindings, setResearchFindings] = useState('');
  const [sources, setSources] = useState(['']);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const isValid = researchFindings.trim().length >= 50;

  const addSource = () => setSources((prev) => [...prev, '']);
  const removeSource = (i) => setSources((prev) => prev.filter((_, idx) => idx !== i));
  const updateSource = (i, val) => {
    setSources((prev) => {
      const updated = [...prev];
      updated[i] = val;
      return updated;
    });
  };

  const isAllowedFile = (file) => {
    if (file.type.startsWith('image/')) return true;
    if (file.type === 'application/pdf') return true;
    if (file.type === 'application/msword') return true;
    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return true;
    const name = file.name.toLowerCase();
    return name.endsWith('.pdf') || name.endsWith('.doc') || name.endsWith('.docx');
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const invalid = files.filter((f) => !isAllowedFile(f));
    if (invalid.length > 0) {
      toast({
        title: 'Invalid file type',
        description: 'Only images, PDFs, .doc and .docx files are allowed.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      e.target.value = '';
      return;
    }
    setAttachmentFiles((prev) => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!currentUser || !userData || !isValid) return;
    setSubmitting(true);

    try {
      let attachmentUrls = [];
      if (attachmentFiles.length > 0) {
        const tempId = `temp_${Date.now()}`;
        attachmentUrls = await uploadFiles(attachmentFiles, `claims/${tempId}`);
      }

      const cleanSources = sources.map((s) => s.trim()).filter(Boolean);

      const isAnon = userData.isFullyAnonymous === true;
      const claimId = await createClaim(
        { researchFindings: researchFindings.trim(), attachmentUrls, sources: cleanSources },
        bountyId,
        currentUser.uid,
        isAnon ? 'Anonymous' : userData.anonymizedUsername,
        isAnon ? null : `${userData.firstName} ${userData.lastName}`.trim()
      );

      toast({
        title: 'Submission received!',
        description: 'Your research has been submitted for review.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate(`/submissions/${claimId}`);
    } catch (err) {
      toast({
        title: 'Error submitting research',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setSubmitting(false);
    }
  };

  if (bountyLoading) return <LoadingSpinner message="Loading task..." />;

  if (!bounty) {
    return (
      <Container maxW="2xl" py={8} textAlign="center">
        <Heading size="md" mb={4}>Task not found</Heading>
        <Button as={Link} to="/tasks" leftIcon={<Icon as={FiArrowLeft} />}>
          Back to Tasks
        </Button>
      </Container>
    );
  }

  if (currentUser?.uid === bounty.creatorUid) {
    return (
      <Container maxW="2xl" py={8} textAlign="center">
        <Heading size="md" mb={2}>You cannot submit to your own task</Heading>
        <Button as={Link} to={`/tasks/${bountyId}`} leftIcon={<Icon as={FiArrowLeft} />} mt={4}>
          Back to Task
        </Button>
      </Container>
    );
  }

  if (['completed', 'cancelled'].includes(bounty.status)) {
    return (
      <Container maxW="2xl" py={8} textAlign="center">
        <Heading size="md" mb={2}>
          This task is no longer accepting submissions
        </Heading>
        <Button as={Link} to={`/tasks/${bountyId}`} leftIcon={<Icon as={FiArrowLeft} />} mt={4}>
          Back to Task
        </Button>
      </Container>
    );
  }

  // Verification gate — must be verified to submit claims
  if (userData && userData.verificationStatus !== 'verified') {
    const status = userData.verificationStatus || 'unverified';
    const messages = {
      unverified: {
        title: 'Verification Required',
        description: 'You must be verified before you can submit research on tasks.',
        buttonText: 'Apply for Verification',
        buttonLink: '/verify',
      },
      pending: {
        title: 'Application Under Review',
        description: 'Your verification application is being reviewed by our team. You\'ll be able to submit research once approved.',
        buttonText: 'View Application Status',
        buttonLink: '/verify',
      },
      rejected: {
        title: 'Verification Required',
        description: 'Your verification application was not approved. Please contact support for assistance.',
        buttonText: 'Contact Support',
        buttonLink: '/support/create',
      },
    };
    const msg = messages[status] || messages.unverified;

    return (
      <Container maxW="2xl" py={8}>
        <VStack spacing={6} align="stretch">
          <Button
            as={Link}
            to={`/tasks/${bountyId}`}
            variant="ghost"
            leftIcon={<Icon as={FiArrowLeft} />}
            alignSelf="flex-start"
            size="sm"
          >
            Back to Task
          </Button>
          <Box
            bg={cardBg}
            rounded="xl"
            p={8}
            boxShadow="sm"
            borderWidth={1}
            borderColor={borderColor}
            textAlign="center"
          >
            <Icon as={FiUserCheck} boxSize={12} color="orange.400" mb={4} />
            <Heading size="md" mb={2}>{msg.title}</Heading>
            <Text color="gray.600" mb={6}>{msg.description}</Text>
            <Button as={Link} to={msg.buttonLink} colorScheme="orange">
              {msg.buttonText}
            </Button>
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="2xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Button
          as={Link}
          to={`/tasks/${bountyId}`}
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          Back to Task
        </Button>

        <Box>
          <Heading size="lg">Submit Research</Heading>
          <Text color="gray.600" mt={1}>
            For: <strong>{bounty.title}</strong> · Reward:{' '}
            <strong><Price cents={bounty.rewardAmount} /></strong>
          </Text>
        </Box>

        <Box
          bg={cardBg}
          rounded="xl"
          p={8}
          boxShadow="sm"
          borderWidth={1}
          borderColor={borderColor}
        >
          <VStack spacing={6} align="stretch">
            {/* Research Findings */}
            <FormControl isRequired>
              <FormLabel fontWeight="semibold">Research Findings</FormLabel>
              <Textarea
                placeholder="Describe your research findings in detail. Include all evidence examined, methodology used, sources consulted, and your conclusions..."
                value={researchFindings}
                onChange={(e) => setResearchFindings(e.target.value)}
                rows={10}
                resize="vertical"
              />
              <FormHelperText>
                {researchFindings.length} characters (min 50) — be thorough and specific
              </FormHelperText>
            </FormControl>

            {/* Sources */}
            <FormControl>
              <FormLabel fontWeight="semibold">Sources & References</FormLabel>
              <VStack spacing={2} align="stretch">
                {sources.map((src, i) => (
                  <HStack key={i}>
                    <Input
                      placeholder="e.g. Getty Provenance Index, Christie's lot #1234, https://..."
                      value={src}
                      onChange={(e) => updateSource(i, e.target.value)}
                      size="sm"
                    />
                    {sources.length > 1 && (
                      <IconButton
                        icon={<Icon as={FiTrash2} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Remove source"
                        onClick={() => removeSource(i)}
                      />
                    )}
                  </HStack>
                ))}
                <Button
                  leftIcon={<Icon as={FiPlus} />}
                  variant="ghost"
                  size="sm"
                  onClick={addSource}
                  alignSelf="flex-start"
                  isDisabled={sources.length >= 10}
                >
                  Add Source
                </Button>
              </VStack>
            </FormControl>

            {/* File Attachments */}
            <FormControl>
              <FormLabel fontWeight="semibold">Supporting Documents</FormLabel>
              <Box
                as="label"
                display="block"
                htmlFor="attachment-upload"
                border="2px dashed"
                borderColor={borderColor}
                rounded="lg"
                p={4}
                cursor="pointer"
                _hover={{ borderColor: 'orange.300' }}
                textAlign="center"
                transition="border-color 0.15s"
              >
                <Text fontSize="sm" color="gray.500">
                  Click to attach files (images, PDF, .doc, .docx) · Max 5 files
                </Text>
                <input
                  id="attachment-upload"
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </Box>
              {attachmentFiles.length > 0 && (
                <VStack align="stretch" mt={2} spacing={1}>
                  {attachmentFiles.map((f, i) => (
                    <HStack key={i} fontSize="sm">
                      <Text flex="1" isTruncated>{f.name}</Text>
                      <IconButton
                        icon={<Icon as={FiTrash2} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Remove"
                        onClick={() =>
                          setAttachmentFiles((prev) => prev.filter((_, idx) => idx !== i))
                        }
                      />
                    </HStack>
                  ))}
                </VStack>
              )}
              {uploading && (
                <Text fontSize="sm" mt={2} color="gray.500">
                  Uploading... {progress}%
                </Text>
              )}
            </FormControl>
          </VStack>
        </Box>

        <HStack justify="flex-end">
          <Button
            onClick={handleSubmit}
            isDisabled={!isValid}
            isLoading={submitting || uploading}
            loadingText={uploading ? `Uploading... ${progress}%` : 'Submitting...'}
            colorScheme="orange"
            size="lg"
          >
            Submit Research
          </Button>
        </HStack>
      </VStack>
    </Container>
  );
}
